import type { ReactNode } from 'react';
import { cn } from './util';

export interface TableColumn<Row> {
  key: string;
  header: ReactNode;
  /** Cell renderer; defaults to `row[key]`. */
  render?: (row: Row, index: number) => ReactNode;
  align?: 'left' | 'center' | 'right';
  /** CSS width (e.g. "5rem", "30%"). */
  width?: string;
  /** Visually emphasise the column (bold, tinted). */
  strong?: boolean;
}

export interface TableProps<Row> {
  columns: readonly TableColumn<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row, index: number) => string;
  caption?: ReactNode;
  /** Optional per-row class (e.g. team tint). */
  rowClassName?: (row: Row, index: number) => string | undefined;
  /** Empty-state text. */
  empty?: ReactNode;
  dense?: boolean;
  className?: string;
  /** Sticky header for long tables inside a scroll container. */
  stickyHeader?: boolean;
}

const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' } as const;

/** Ink-ruled stats table: black header with yellow Bangers, ink grid, paper zebra rows. */
export function Table<Row>({
  columns,
  rows,
  rowKey,
  caption,
  rowClassName,
  empty = 'Henüz veri yok.',
  dense,
  className,
  stickyHeader,
}: TableProps<Row>) {
  const pad = dense ? 'px-2 py-1' : 'px-3 py-1.5';
  return (
    // `relative` makes the scroller the containing block of absolutely positioned cell content
    // (e.g. `sr-only` headers): otherwise it escapes the scroller at the table's far edge and
    // widens the page, so phones zoom the whole page out instead of scrolling the table.
    <div
      className={cn(
        'relative max-w-full overflow-x-auto border-3 border-ink bg-white shadow-ink-sm',
        className,
      )}
    >
      <table className="w-full border-collapse text-[14px] tabular-nums">
        {caption && (
          <caption className="comic border-b-3 border-ink bg-yellow px-3 py-1.5 text-left text-[18px]">
            {caption}
          </caption>
        )}
        <thead className={cn(stickyHeader && 'sticky top-0 z-10')}>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                style={c.width ? { width: c.width } : undefined}
                className={cn(
                  'comic border-2 border-ink bg-ink text-[16px] font-normal text-yellow first:border-l-0 last:border-r-0',
                  pad,
                  ALIGN[c.align ?? 'center'],
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                className="px-3 py-6 text-center font-bold text-ink-soft"
              >
                {empty}
              </td>
            </tr>
          )}
          {rows.map((row, i) => (
            <tr
              key={rowKey(row, i)}
              className={cn('even:bg-paper/70 hover:bg-yellow-soft/60', rowClassName?.(row, i))}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={cn(
                    'border-2 border-ink first:border-l-0 last:border-r-0',
                    pad,
                    ALIGN[c.align ?? 'center'],
                    c.strong ? 'font-black' : 'font-bold',
                  )}
                >
                  {c.render
                    ? c.render(row, i)
                    : String((row as Record<string, unknown>)[c.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
